import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, LogOut, FileText, Sparkles, Package, User, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import Brand from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const NAV = [
  { key: "overview", label: "portal.overview", to: "/dashboard", icon: LayoutDashboard },
  { key: "orders", label: "portal.orders", to: "/orders", icon: Package },
  { key: "profile", label: "portal.profile", to: "/profile", icon: User },
  { key: "services", label: "portal.services", to: "/company-formation", icon: FileText },
  { key: "advisor", label: "portal.aiAdvisor", to: "/advisor", icon: Sparkles },
];

function NavLinks({ active, onNavigate }) {
  const { t } = useTranslation();
  return (
    <nav className="flex-1 px-3 py-5 space-y-1">
      {NAV.map(({ key, label, to, icon: Icon }) => {
        const isActive = key === active;
        const cls = isActive
          ? "flex items-center gap-3 rounded-sm px-3 py-2 text-sm bg-secondary text-foreground"
          : "flex items-center gap-3 rounded-sm px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/60";
        return isActive ? (
          <span key={key} className={cls} data-testid={`portal-nav-${key}`}><Icon className="h-4 w-4" /> {t(label)}</span>
        ) : (
          <Link key={key} to={to} onClick={onNavigate} className={cls} data-testid={`portal-nav-${key}`}><Icon className="h-4 w-4" /> {t(label)}</Link>
        );
      })}
    </nav>
  );
}

export default function PortalLayout({ active, children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const UserFooter = (
    <div className="border-t border-border p-3">
      <div className="flex items-center gap-3 px-2 py-2">
        <Avatar className="h-8 w-8 border border-border"><AvatarImage src={user?.picture} alt={user?.name} /><AvatarFallback className="bg-secondary text-xs">{(user?.name || "U").slice(0, 1)}</AvatarFallback></Avatar>
        <div className="min-w-0"><p className="text-sm truncate">{user?.name}</p><p className="text-xs text-muted-foreground truncate">{user?.email}</p></div>
      </div>
      <Button variant="ghost" className="w-full justify-start mt-1 text-muted-foreground" onClick={handleLogout} data-testid="portal-logout"><LogOut className="h-4 w-4 mr-2" /> {t("client.signOut")}</Button>
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card fixed inset-y-0">
        <div className="flex items-center px-6 h-16 border-b border-border"><Brand size="sm" to="/dashboard" /></div>
        <NavLinks active={active} />
        {UserFooter}
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between h-14 px-4 border-b border-border bg-card">
        <Brand size="sm" to="/dashboard" />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="portal-mobile-toggle"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 p-0 flex flex-col" data-testid="portal-mobile-menu">
            <div className="flex items-center px-6 h-16 border-b border-border"><Brand size="sm" to="/dashboard" /></div>
            <NavLinks active={active} onNavigate={() => setOpen(false)} />
            {UserFooter}
          </SheetContent>
        </Sheet>
      </header>

      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0" data-testid={`portal-${active}`}>
        {children}
      </main>
    </div>
  );
}
